/**
 * VolumeChart — Trading volume histogram using Lightweight Charts (TradingView).
 *
 * Owns its own data fetching via useVolumeHistory hook.
 * Mirrors PriceChart structure: async dynamic import, resize observer, sample data badge.
 *
 * Story 8.3 — Task 2
 */

import type { PriceInterval, VolumeHistoryEntry } from "@repo/shared-types";
import {
	ColorType,
	type IChartApi,
	type ISeriesApi,
	type SeriesOptionsMap,
} from "lightweight-charts";
import { useEffect, useRef, useState } from "react";

import { useVolumeHistory } from "../../hooks/api/use-volume-history";
import { toUTCTimestamp } from "../../lib/chart-utils";
import { TimeRangeSelector } from "./TimeRangeSelector";

type VolumeChartProps = {
	tokenId: string;
};

export function VolumeChart({ tokenId }: VolumeChartProps) {
	const [selectedInterval, setSelectedInterval] = useState<PriceInterval>("1D");
	const { data, isLoading, error, refetch } = useVolumeHistory(tokenId, selectedInterval);
	const [chartReady, setChartReady] = useState(false);

	const containerRef = useRef<HTMLDivElement>(null);
	const chartRef = useRef<IChartApi | null>(null);
	const seriesRef = useRef<ISeriesApi<keyof SeriesOptionsMap> | null>(null);
	const observerRef = useRef<ResizeObserver | null>(null);

	// Mount/unmount chart — async import matching PriceChart pattern
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		let cancelled = false;

		async function initChart() {
			const { createChart, HistogramSeries } = await import("lightweight-charts");
			if (cancelled || !container) return;

			const chart: IChartApi = createChart(container, {
				width: container.clientWidth,
				height: 300,
				layout: {
					background: { type: ColorType.Solid, color: "transparent" },
					textColor: "#6b7280",
				},
				grid: {
					vertLines: { color: "rgba(100, 100, 100, 0.1)" },
					horzLines: { color: "rgba(100, 100, 100, 0.1)" },
				},
				rightPriceScale: {
					borderVisible: false,
				},
				timeScale: {
					borderVisible: false,
					timeVisible: selectedInterval === "1H" || selectedInterval === "4H",
				},
			});

			const series = chart.addSeries(HistogramSeries, {
				color: "#3b82f6",
				priceFormat: { type: "volume" },
			});

			chartRef.current = chart;
			seriesRef.current = series;

			const observer = new ResizeObserver((entries) => {
				const entry = entries[0];
				if (entry) {
					chart.applyOptions({ width: entry.contentRect.width });
				}
			});
			observer.observe(container);
			observerRef.current = observer;

			if (!cancelled) setChartReady(true);
		}

		void initChart();

		return () => {
			cancelled = true;
			observerRef.current?.disconnect();
			observerRef.current = null;
			if (chartRef.current) {
				chartRef.current.remove();
				chartRef.current = null;
				seriesRef.current = null;
			}
			setChartReady(false);
		};
	}, [selectedInterval]);

	// Update data — depends on chartReady to avoid race condition with async init
	useEffect(() => {
		const series = seriesRef.current;
		if (!series || !chartReady || !data?.data) return;

		const chartData = data.data.map((entry: VolumeHistoryEntry) => ({
			time: toUTCTimestamp(entry.timestamp),
			value: entry.volume,
			color: "#3b82f6",
		}));

		series.setData(chartData);
		chartRef.current?.timeScale().fitContent();
	}, [data, chartReady]);

	const isSeeded = data?.meta?.dataSource === "seeded";

	return (
		<div className="space-y-3">
			<div className="flex items-center justify-between">
				<TimeRangeSelector selected={selectedInterval} onIntervalChange={setSelectedInterval} />
				{isSeeded && (
					<span className="text-xs text-pastel-muted bg-yellow-50 px-2 py-0.5 rounded">
						Sample Data
					</span>
				)}
			</div>

			{error ? (
				<div className="flex flex-col items-center justify-center h-[300px] text-pastel-muted text-sm gap-2">
					<p>Failed to load volume data</p>
					<button
						type="button"
						onClick={() => void refetch()}
						className="text-pastel-primary text-xs font-medium hover:underline"
					>
						Retry
					</button>
				</div>
			) : data?.data?.length === 0 && !isLoading ? (
				<div className="flex items-center justify-center h-[300px] text-pastel-muted text-sm">
					No volume data available
				</div>
			) : (
				<>
					{isLoading && !chartReady && <VolumeChartSkeleton />}
					<div
						ref={containerRef}
						className="w-full h-[300px]"
						style={
							isLoading && !chartReady ? { visibility: "hidden", position: "absolute" } : undefined
						}
					/>
				</>
			)}
		</div>
	);
}

function VolumeChartSkeleton() {
	return <div className="h-[300px] w-full rounded-xl bg-pastel-surface animate-pulse" />;
}
